import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Shield, 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Mail,
  Check,
  X,
  Lock,
  User,
  Clock,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminPage() {
  const { hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('roles');
  
  // Roles state
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Invitations state
  const [invitations, setInvitations] = useState([]);
  
  // Dialog state
  const [roleDialog, setRoleDialog] = useState({ open: false, mode: 'create', role: null });
  const [userDialog, setUserDialog] = useState({ open: false, mode: 'create', user: null });
  const [inviteDialog, setInviteDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });

  // Fetch data
  const fetchRoles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      toast.error('Failed to fetch roles');
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/permissions`);
      setPermissions(response.data);
    } catch (error) {
      toast.error('Failed to fetch permissions');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/invitations`);
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to fetch invitations');
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchUsers();
    fetchInvitations();
  }, [fetchRoles, fetchPermissions, fetchUsers, fetchInvitations]);

  // Check admin access
  if (!hasRole('admin')) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Lock className="w-16 h-16 text-zinc-400 dark:text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-zinc-900 dark:text-white mb-2">Access Denied</h2>
            <p className="text-zinc-500 dark:text-zinc-400">You don&apos;t have permission to access this page.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">Admin Panel</h1>
          <p className="text-zinc-500 dark:text-zinc-400 mt-1">Manage roles, permissions, and users</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-200 dark:border-zinc-800 pb-2">
        <Button
          variant={activeTab === 'roles' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('roles')}
          className={activeTab === 'roles' ? 'bg-[#C3002F]' : ''}
        >
          <Shield className="w-4 h-4 mr-2" />
          Roles & Permissions
        </Button>
        <Button
          variant={activeTab === 'users' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('users')}
          className={activeTab === 'users' ? 'bg-[#C3002F]' : ''}
        >
          <Users className="w-4 h-4 mr-2" />
          Users
        </Button>
        <Button
          variant={activeTab === 'invitations' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('invitations')}
          className={activeTab === 'invitations' ? 'bg-[#C3002F]' : ''}
        >
          <Mail className="w-4 h-4 mr-2" />
          Invitations
          {invitations.filter(i => i.status === 'pending').length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-amber-500 rounded-full">
              {invitations.filter(i => i.status === 'pending').length}
            </span>
          )}
        </Button>
      </div>

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <RolesTab
          roles={roles}
          permissions={permissions}
          loading={loadingRoles}
          onEdit={(role) => setRoleDialog({ open: true, mode: 'edit', role })}
          onCreate={() => setRoleDialog({ open: true, mode: 'create', role: null })}
          onDelete={(role) => setDeleteDialog({ open: true, type: 'role', item: role })}
          onRefresh={fetchRoles}
        />
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <UsersTab
          users={users}
          roles={roles}
          loading={loadingUsers}
          onEdit={(user) => setUserDialog({ open: true, mode: 'edit', user })}
          onCreate={() => setUserDialog({ open: true, mode: 'create', user: null })}
          onInvite={() => setInviteDialog(true)}
          onDelete={(user) => setDeleteDialog({ open: true, type: 'user', item: user })}
          onRefresh={fetchUsers}
        />
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <InvitationsTab
          invitations={invitations}
          roles={roles}
          onInvite={() => setInviteDialog(true)}
          onCancel={(inv) => setDeleteDialog({ open: true, type: 'invitation', item: inv })}
          onRefresh={fetchInvitations}
        />
      )}

      {/* Role Dialog */}
      <RoleDialog
        open={roleDialog.open}
        mode={roleDialog.mode}
        role={roleDialog.role}
        permissions={permissions}
        onClose={() => setRoleDialog({ open: false, mode: 'create', role: null })}
        onSave={fetchRoles}
      />

      {/* User Dialog */}
      <UserDialog
        open={userDialog.open}
        mode={userDialog.mode}
        user={userDialog.user}
        roles={roles}
        onClose={() => setUserDialog({ open: false, mode: 'create', user: null })}
        onSave={fetchUsers}
      />

      {/* Invite Dialog */}
      <InviteDialog
        open={inviteDialog}
        roles={roles}
        onClose={() => setInviteDialog(false)}
        onSave={() => {
          fetchInvitations();
          setInviteDialog(false);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialog.open}
        type={deleteDialog.type}
        item={deleteDialog.item}
        onClose={() => setDeleteDialog({ open: false, type: null, item: null })}
        onConfirm={() => {
          if (deleteDialog.type === 'role') fetchRoles();
          if (deleteDialog.type === 'user') fetchUsers();
          if (deleteDialog.type === 'invitation') fetchInvitations();
          setDeleteDialog({ open: false, type: null, item: null });
        }}
      />
      </div>
    </MainLayout>
  );
}

// ==================== ROLES TAB ====================
function RolesTab({ roles, permissions, loading, onEdit, onCreate, onDelete }) {
  // Group permissions by category
  const permissionsByCategory = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {});

  if (loading) {
    return <div className="text-center text-zinc-500 dark:text-zinc-400 py-8">Loading roles...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Roles ({roles.length})</h2>
        <Button onClick={onCreate} className="bg-[#C3002F] hover:bg-[#9f0026]">
          <Plus className="w-4 h-4 mr-2" />
          Create Role
        </Button>
      </div>

      <div className="grid gap-4">
        {roles.map((role) => (
          <div
            key={role.id}
            className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-zinc-900 dark:text-white">{role.name}</h3>
                  {role.is_system && (
                    <span className="px-2 py-0.5 text-xs bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 rounded">
                      System
                    </span>
                  )}
                  {!role.is_active && (
                    <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">{role.description || 'No description'}</p>
                
                {/* Permissions summary */}
                <div className="mt-3 flex flex-wrap gap-1">
                  {role.permissions.slice(0, 8).map((perm) => (
                    <span
                      key={perm}
                      className="px-2 py-0.5 text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded"
                    >
                      {perm}
                    </span>
                  ))}
                  {role.permissions.length > 8 && (
                    <span className="px-2 py-0.5 text-xs bg-zinc-800 text-zinc-400 rounded">
                      +{role.permissions.length - 8} more
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-zinc-700"
                  onClick={() => onEdit(role)}
                  disabled={role.is_system}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-800 text-red-400 hover:bg-red-500/10"
                  onClick={() => onDelete(role)}
                  disabled={role.is_system}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Permissions Reference */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">Available Permissions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(permissionsByCategory).map(([category, perms]) => (
            <div key={category} className="p-3 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg">
              <h4 className="font-medium text-zinc-700 dark:text-zinc-300 mb-2">{category}</h4>
              <div className="space-y-1">
                {perms.map((perm) => (
                  <div key={perm.key} className="text-xs text-zinc-500">
                    <code className="text-zinc-600 dark:text-zinc-400">{perm.key}</code>
                    <span className="ml-2">- {perm.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==================== USERS TAB ====================
function UsersTab({ users, roles, loading, onEdit, onCreate, onInvite, onDelete }) {
  const [search, setSearch] = useState('');

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="text-center text-zinc-500 dark:text-zinc-400 py-8">Loading users...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4">
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700"
        />
        <div className="flex gap-2">
          <Button onClick={onInvite} variant="outline" className="border-zinc-700">
            <Mail className="w-4 h-4 mr-2" />
            Invite User
          </Button>
          <Button onClick={onCreate} className="bg-[#C3002F] hover:bg-[#9f0026]">
            <Plus className="w-4 h-4 mr-2" />
            Create User
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-200 dark:border-zinc-800">
              <th className="text-left py-3 px-4 text-sm font-medium text-zinc-600 dark:text-zinc-400">User</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-zinc-600 dark:text-zinc-400">Role</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-zinc-600 dark:text-zinc-400">Status</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-zinc-600 dark:text-zinc-400">Created</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-zinc-600 dark:text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className="border-b border-zinc-200 dark:border-zinc-800/50 hover:bg-zinc-50 dark:hover:bg-zinc-900/30">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full  dark:bg-zinc-700 bg-gray-200 flex items-center justify-center">
                      <User className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                    </div>
                    <div>
                      <p className="font-medium text-zinc-900 dark:text-white">{user.name}</p>
                      <p className="text-sm text-zinc-500">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-1 text-xs rounded ${
                    user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                    user.role === 'approver' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {user.role_name || user.role}
                  </span>
                </td>
                <td className="py-3 px-4">
                  {user.is_active ? (
                    <span className="flex items-center gap-1 text-emerald-400 text-sm">
                      <Check className="w-4 h-4" /> Active
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-400 text-sm">
                      <X className="w-4 h-4" /> Inactive
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-sm text-zinc-600 dark:text-zinc-400">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="py-3 px-4">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-700"
                      onClick={() => onEdit(user)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-800 text-red-400 hover:bg-red-500/10"
                      onClick={() => onDelete(user)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center text-zinc-400 py-8">No users found</div>
      )}
    </div>
  );
}

// ==================== INVITATIONS TAB ====================
function InvitationsTab({ invitations, roles, onInvite, onCancel }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">
          Invitations ({invitations.filter(i => i.status === 'pending').length} pending)
        </h2>
        <Button onClick={onInvite} className="bg-[#C3002F] hover:bg-[#9f0026]">
          <Plus className="w-4 h-4 mr-2" />
          Send Invitation
        </Button>
      </div>

      {invitations.length === 0 ? (
        <div className="text-center text-zinc-400 py-8">No invitations sent yet</div>
      ) : (
        <div className="space-y-2">
          {invitations.map((inv) => (
            <div
              key={inv.id}
              className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-zinc-500" />
                <div>
                  <p className="text-white">{inv.email}</p>
                  <p className="text-sm text-zinc-500">
                    Role: {inv.role_name} • Invited by {inv.invited_by_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`px-2 py-1 text-xs rounded ${
                  inv.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                  inv.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {inv.status}
                </span>
                {inv.status === 'pending' && (
                  <>
                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Expires {new Date(inv.expires_at).toLocaleDateString()}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-800 text-red-400 hover:bg-red-500/10"
                      onClick={() => onCancel(inv)}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== ROLE DIALOG ====================
function RoleDialog({ open, mode, role, permissions, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (role && mode === 'edit') {
      setFormData({
        name: role.name,
        description: role.description || '',
        permissions: role.permissions || []
      });
    } else {
      setFormData({ name: '', description: '', permissions: [] });
    }
  }, [role, mode, open]);

  const handlePermissionToggle = (permKey) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter((p) => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    setSaving(true);
    try {
      if (mode === 'create') {
        await axios.post(`${API}/roles`, formData);
        toast.success('Role created successfully');
      } else {
        await axios.put(`${API}/roles/${role.id}`, formData);
        toast.success('Role updated successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  // Group permissions by category
  const permissionsByCategory = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="">
            {mode === 'create' ? 'Create New Role' : 'Edit Role'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-zinc-700 dark:text-zinc-400">Role Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Senior Approver"
              className=" mt-1"
            />
          </div>

          <div>
            <Label className="text-zinc-700 dark:text-zinc-400">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this role can do..."
              className=" mt-1"
            />
          </div>

          <div>
            <Label className="text-zinc-700 dark:text-zinc-400 mb-3 block">Permissions</Label>
            <div className="space-y-4 max-h-60 overflow-y-auto">
              {Object.entries(permissionsByCategory).map(([category, perms]) => (
                <div key={category} className="p-3 dark:bg-zinc-800/50 rounded-lg">
                  <h4 className="font-medium dark:text-zinc-300 mb-2">{category}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {perms.map((perm) => (
                      <div key={perm.key} className="flex items-center gap-2">
                        <Checkbox
                          id={perm.key}
                          checked={formData.permissions.includes(perm.key)}
                          onCheckedChange={() => handlePermissionToggle(perm.key)}
                        />
                        <label htmlFor={perm.key} className="text-sm dark:text-zinc-400 cursor-pointer">
                          {perm.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#C3002F] hover:bg-[#9f0026]">
            {saving ? 'Saving...' : mode === 'create' ? 'Create Role' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== USER DIALOG ====================
function UserDialog({ open, mode, user, roles, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role_id: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && mode === 'edit') {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role_id: user.role_id || '',
        is_active: user.is_active !== false
      });
    } else {
      setFormData({ name: '', email: '', password: '', role_id: '', is_active: true });
    }
  }, [user, mode, open]);

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    if (mode === 'create' && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }

    setSaving(true);
    try {
      if (mode === 'create') {
        await axios.post(`${API}/users`, formData);
        toast.success('User created successfully');
      } else {
        const updateData = { ...formData };
        delete updateData.password; // Don't update password through this endpoint
        await axios.put(`${API}/users/${user.id}`, updateData);
        toast.success('User updated successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800">
        <DialogHeader>
          <DialogTitle className="">
            {mode === 'create' ? 'Create New User' : 'Edit User'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-zinc-700 dark:text-zinc-400">Full Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-zinc-700 dark:text-zinc-400">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@nissan.com"
              className=" mt-1"
            />
          </div>

          {mode === 'create' && (
            <div>
              <Label className="text-zinc-700 dark:text-zinc-400">Password</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className=" mt-1"
              />
            </div>
          )}

          <div>
            <Label className="text-zinc-700 dark:text-zinc-400">Role</Label>
            <Select
              value={formData.role_id}
              onValueChange={(value) => setFormData({ ...formData, role_id: value })}
            >
              <SelectTrigger className=" mt-1">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {mode === 'edit' && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <label htmlFor="is_active" className="text-sm  cursor-pointer">
                Account is active
              </label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#C3002F] hover:bg-[#9f0026]">
            {saving ? 'Saving...' : mode === 'create' ? 'Create User' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== INVITE DIALOG ====================
function InviteDialog({ open, roles, onClose, onSave }) {
  const [formData, setFormData] = useState({ email: '', role_id: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!formData.role_id) {
      toast.error('Please select a role');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/users/invite`, formData);
      toast.success('Invitation sent successfully');
      // Show the invitation token (for demo purposes)
      console.log('Invitation token:', response.data.token);
      setFormData({ email: '', role_id: '' });
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800">
        <DialogHeader>
          <DialogTitle className="">Invite User</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="">Email Address</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="user@nissan.com"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="">Assign Role</Label>
            <Select
              value={formData.role_id}
              onValueChange={(value) => setFormData({ ...formData, role_id: value })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-zinc-500">
            An invitation email will be sent with a link to complete registration.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#C3002F] hover:bg-[#9f0026]">
            {saving ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== DELETE DIALOG ====================
function DeleteDialog({ open, type, item, onClose, onConfirm }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (type === 'role') {
        await axios.delete(`${API}/roles/${item.id}`);
        toast.success('Role deleted');
      } else if (type === 'user') {
        await axios.delete(`${API}/users/${item.id}`);
        toast.success('User deactivated');
      } else if (type === 'invitation') {
        await axios.delete(`${API}/invitations/${item.id}`);
        toast.success('Invitation cancelled');
      }
      onConfirm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800">
        <DialogHeader>
          <DialogTitle className=" flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Confirm {type === 'user' ? 'Deactivation' : 'Deletion'}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="">
            {type === 'role' && `Are you sure you want to delete the role "${item.name}"?`}
            {type === 'user' && `Are you sure you want to deactivate the user "${item.name}"? They will no longer be able to login.`}
            {type === 'invitation' && `Are you sure you want to cancel the invitation for "${item.email}"?`}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleting ? 'Processing...' : type === 'user' ? 'Deactivate' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
